import { useState, useEffect, useRef, useMemo, FormEvent, KeyboardEvent } from "react";
import { createFilterOptions, updateActiveTask, Util } from '../../util/Util'
import { canEditSalePrice, canSellBelowMinPrice, catalogMinSellPrice } from '../../util/salePrice'
import { Item } from '../../util/Item'

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import RefreshButton from "../../components/RefreshButton";
import { SelectInstance } from "react-select";
import { useSale } from "./SaleContext";
import { useAuth } from "../../hooks/useAuth";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MYSQL_LIMITS } from "../../util/constants";
import { MONEY_LIMITS, QTY_LIMITS, isWholeNumber, parseMoneyInput, qtyMin, roundMoney, roundQty } from "../../util/quantity";
import { useUI } from "../../providers/MoleUIContext";
import { toast } from "react-toastify";
import { Autocomplete, Button, Checkbox, Grid, TextField } from "@mui/material";
import { Add } from "@mui/icons-material";
import QuantityPackHelper from "../../components/QuantityPackHelper";
import { focusAndSelect, isPlainEnter } from "../../util/focusField";
import {
	isTillStockChecked,
	isNegativeStockAllowed,
	shouldEnforceStockQuantity,
} from "../../components/quantityStepperStock";
import { renderCatalogItemOption } from "../../components/itemAutocompleteRenderOption";
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from "../../components/catalogItemAutocomplete";
import { CatalogItemPickerProvider } from "../../components/CatalogItemActions";
import VirtualizedListbox from "../../components/VirtualizedListbox";

interface Props {
	bundle?: any;
	/** A saved sale is being amended; otherwise this is a new sale. */
	amending?: boolean;
}


interface OptionType {
	value: number;
	label: string;
}

const SaleItemDetail = ({ bundle, amending = false }: Props) => {
	const lineAction = amending ? "update" : "create";
	const [selectedItem, setSelectedItem] = useState<Item | null>(null);
	const [errorInput, setErrorInput] = useState("");
	const [isItemLoading, setIsItemLoading] = useState(false);
	const [useBarcode, setUseBarcode] = useState(false);
	const [barcode, setBarcode] = useState("");

	const { api, session, access } = useAuth();
	const { appSettings } = useAppSocket();
	const { queueReRun, t } = useUI();
	const {
		setPendingTasks,
		activeTask,
		items,
		setItems,
		barcodes,
		setBarcodes,
		itemsSourceId,
		setItemsSourceId,
		apiRef,
	} = useSale();

	const barcodeRef = useRef<HTMLInputElement>(null);
	const itemRef = useRef<SelectInstance<OptionType, false>>(null);
	const quantityRef = useRef<HTMLInputElement>(null);
	const amountRef = useRef<HTMLInputElement>(null);
	const addBtnRef = useRef<HTMLButtonElement>(null);

	const errorMessageRef = useRef<string>("");
	const toastIdRef = useRef<string | number>(-1);
	const lastHandledSelectionRef = useRef<{ id: number; at: number } | null>(null);
	const cartItemsRef = useRef<Item[]>([]);

	useEffect(() => {
		cartItemsRef.current = [...(activeTask?.items ?? [])];
	}, [activeTask?.items]);

	const selectedCatalogItem = useMemo(
		() => (selectedItem
			? items.find((catalogEntry: any) => catalogEntry.id === selectedItem.id)
			: null),
		[items, selectedItem],
	);

	const bundleStockSum = useMemo(() => {
		if (!selectedItem || !bundle) return 0;

		return bundle.reduce((sum: number, bundleItem: any) => {
			return parseInt(bundleItem.item_id) === selectedItem.id ? sum + bundleItem.quantity : sum;
		}, 0);
	}, [selectedItem, bundle]);

	const activeStock = useMemo(() => {
		if (!selectedItem) return 0;

		const catalogStock = selectedCatalogItem?.Stock ?? selectedItem.stock ?? 0;
		const taskItemStockSum = (activeTask?.items || []).reduce((sum, item) => (
			item.id === selectedItem.id ? sum + item.quantity : sum
		), 0);

		return roundQty(bundleStockSum + catalogStock - taskItemStockSum);
	}, [selectedItem, selectedCatalogItem, bundleStockSum, activeTask?.items]);

	const isMetered = selectedItem ? isTillStockChecked(selectedCatalogItem) : true;
	const allowNegativeStock = isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value);

	const quantityMax = shouldEnforceStockQuantity({ isMetered, allowNegativeStock })
		? Math.min(QTY_LIMITS.max, Math.max(activeStock, qtyMin(!!selectedItem?.isFractional)))
		: QTY_LIMITS.max;

	const fetchItems = async () => {
		if ((activeTask?.source || -1) < 1) return;

		setIsItemLoading(true);

		const res = await api.get(`items/${activeTask?.source}/sales`);

		if (res) {
			if (res.status === 200) {
				setItems(res.data.items);
				setBarcodes(res.data.barcodes || {});
				setItemsSourceId(activeTask?.source ?? null);
			} else if (res.status === 401 || res.status === 422) {
				queueReRun(fetchItems);
			} else {
				console.error("Failed to fetch data:", res);
			}
		}

		setIsItemLoading(false);
	};

	const shouldAutoAdd = () =>
		Util.isBoolSettingEnabled(appSettings?.AUTO_ITEM_ADD?.value, true);

	const getItemStockInfo = (item: Item) => {
		const catalogItem = items.find((catalogEntry: any) => catalogEntry.id === item.id);
		const catalogStock = catalogItem?.Stock ?? item.stock ?? 0;

		const bundleStock = bundle
			? bundle.reduce((sum: number, bundleItem: any) => (
				parseInt(bundleItem.item_id) === item.id ? sum + bundleItem.quantity : sum
			), 0)
			: 0;

		const taskItemStockSum = cartItemsRef.current.reduce((sum, cartItem) => (
			cartItem.id === item.id ? sum + cartItem.quantity : sum
		), 0);

		return {
			isMetered: isTillStockChecked(catalogItem),
			activeStock: roundQty(bundleStock + catalogStock - taskItemStockSum),
		};
	};

	const onItemChanged = (item_id: number) => {
		if (item_id < 1) {
			setSelectedItem(null);
			return;
		}

		const now = Date.now();
		const lastSelection = lastHandledSelectionRef.current;
		if (lastSelection?.id === item_id && now - lastSelection.at < 400) {
			return;
		}
		lastHandledSelectionRef.current = { id: item_id, at: now };

		const item: any = items.find((item: any) => item.id === item_id);

		if (item) {
			const myItem = new Item({
				id: item.id,
				item_code: item.ItemCode,
				item: item.ItemName,
				price: item.SellingPrice,
				stock: item.Stock,
				quantity: 1,
				category: item.Category,
				isFractional: item.IsFractional === true,
				minSellPrice: catalogMinSellPrice(item),
			});

			setSelectedItem(myItem);

			if (shouldAutoAdd()) {
				addItemToCart(myItem);
			} else {
				focusAndSelect(quantityRef);
			}
		} else {
			setSelectedItem(null);
			lastHandledSelectionRef.current = null;
		}
	};

	const lookupBarcode = (code: string = barcode) => {
		const itemID = barcodes[code];

		if (itemID) {
			onItemChanged(itemID);
			setBarcode("");
		} else {
			itemRef.current?.focus();
			setErrorInput("barcodeInput");
			setTimeout(() => setErrorInput(""), 2000);
		}
	}


	const validateQty = (value: number, item: Item = selectedItem!): boolean => {
		let error = false;
		const { isMetered: metered, activeStock: stock } = getItemStockInfo(item);

		if (value < qtyMin(!!item.isFractional) || value > QTY_LIMITS.max) {
			error = true;
			errorMessageRef.current = t("common:validation.quantityRange", { max: Util.formatNumber(QTY_LIMITS.max) });
		} else if (!item.isFractional && !isWholeNumber(value)) {
			error = true;
			errorMessageRef.current = t("common:validation.quantityMustBeWhole");
		} else if (
			shouldEnforceStockQuantity({
				isMetered: metered,
				allowNegativeStock: isNegativeStockAllowed(appSettings?.ALLOW_NEGATIVE_STOCK?.value),
			})
			&& value > stock
		) {
			error = true;
			errorMessageRef.current = t("common:validation.insufficientStock");
		}

		if (error) {
			setErrorInput("quantityInput");
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 2000);
		}

		return !error;
	}

	const validateAmt = (value: number, item: Item | null = selectedItem): boolean => {
		if (value < 0 || value > MONEY_LIMITS.max) {
			setErrorInput("amountInput");
			errorMessageRef.current = t("common:validation.amountRange", { max: Util.formatNumber(MONEY_LIMITS.max) });
			setTimeout(() => {
				setErrorInput("");
				errorMessageRef.current = ""
			}, 2000);
			return false;
		}
		if (!canSellBelowMinPrice(access, lineAction)) {
			const catalog = item ? items.find((c: any) => c.id === item.id) : null;
			const minSell = catalogMinSellPrice(catalog, item);
			if (minSell > 0 && value < minSell) {
				setErrorInput("amountInput");
				errorMessageRef.current = t("common:validation.priceBelowMinSell", {
					min: Util.formatNumber(minSell),
					item: item?.item || "",
				});
				setTimeout(() => {
					setErrorInput("");
					errorMessageRef.current = ""
				}, 2000);
				return false;
			}
		}
		return true;
	}

	const focusEntryField = () => {
		focusAndSelect(useBarcode ? barcodeRef : itemRef);
	};

	// Enter in quantity moves to the price; Enter there adds the line (form submit).
	const onQuantityKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		if (!isPlainEnter(event) || !canEditSalePrice(access, lineAction)) return;
		event.preventDefault();
		focusAndSelect(amountRef);
	};

	const addItemToCart = (item: Item) => {
		if (!activeTask) return false;

		if (!validateQty(item.quantity, item) || !validateAmt(item.price)) return false;

		let added = false;

		updateActiveTask(setPendingTasks, (prev) => {
			const cartItems = prev.items ?? [];
			const existingIndex = cartItems.findIndex(
				(row) => Util.compareStrings(row.uniqueId, item.uniqueId),
			);

			const mergedRow = new Item({ ...item });

			if (existingIndex > -1) {
				mergedRow.quantity = roundQty(mergedRow.quantity + cartItems[existingIndex].quantity);

				if (mergedRow.quantity > QTY_LIMITS.max) {
					const rowIndex = apiRef.current
						? apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId) + 1
						: existingIndex + 1;
					errorMessageRef.current = t("common:validation.mergeExceedsLimit", {
						row: rowIndex,
						max: Util.formatNumber(QTY_LIMITS.max),
					});
					setErrorInput("quantityInput");
					setTimeout(() => {
						setErrorInput("");
						errorMessageRef.current = "";
					}, 5000);
					return {};
				}

				if (apiRef.current) {
					const oldIndex = apiRef.current.getRowIndexRelativeToVisibleRows(mergedRow.uniqueId);
					const rowIndex = oldIndex + 1;
					const msg = t("common:info.mergedWithRow", { row: rowIndex, quantity: mergedRow.quantity });
					if (!toast.isActive(toastIdRef.current)) {
						toastIdRef.current = toast.info(msg, { autoClose: 5000 });
					} else {
						toast.update(toastIdRef.current, { render: msg, autoClose: 5000 });
					}
				}
			}

			const updatedItems = existingIndex > -1
				? cartItems.map((cartItem, idx) => (idx === existingIndex ? mergedRow : cartItem))
				: [...cartItems, mergedRow];

			cartItemsRef.current = updatedItems;
			added = true;
			return { items: updatedItems };
		});

		if (added) {
			focusEntryField();
		}

		return added;
	};

	const onAddClicked = (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();

		if (selectedItem) {
			addItemToCart(selectedItem);
		} else {
			focusEntryField();
		}
	};

	useEffect(() => {
		if (useBarcode) {
			barcodeRef.current?.focus();
		} else {
			itemRef.current?.focus();
		}
	}, [useBarcode]);

	// Auto-load once per depot when POS opens (or depot changes). Manual refresh only after that.
	// Layout switches remount this panel but shared SaleContext cache avoids re-fetch.
	useEffect(() => {
		const source = activeTask?.source ?? -1;
		if (source < 1 || isItemLoading) return;
		if (itemsSourceId === source && items.length > 0) return;
		void fetchItems();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeTask?.source]);

	useEffect(() => {
		setUseBarcode(Util.isBoolSettingEnabled(appSettings?.UI_ENABLE_BARCODE?.value, true));
	}, []);



	return (
		<form onSubmit={onAddClicked} style={{ marginBottom: '10px' }}>
			<Grid container rowSpacing={0} columnSpacing={0}>
				<Grid size={{ xs: 12, sm: 6, md: 3 }} >
					<TextField
						id="barcodeInput"
						size="small"
						label={
							<span >
								{t("common:ui.labels.barcode")}&nbsp;&nbsp;
								<Checkbox
									checked={useBarcode}
									onChange={() => setUseBarcode(!useBarcode)}
								/>
							</span>
						}
						variant="filled"
						fullWidth
						type="text"
						value={barcode}
						inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
						inputRef={barcodeRef}
						slotProps={{
							input: {
								endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupBarcode()} />,
							}
						}}
						onFocus={(e) => e.target.select()}
						onKeyDown={(e) => {
							if (e.key === "Enter") {
								e.preventDefault();
								lookupBarcode(barcode);
							}
						}}
						onChange={(e) => setBarcode(e.target.value)}
						disabled={!activeTask || isItemLoading || Object.keys(barcodes).length === 0}
						error={errorInput === "barcodeInput"}
						helperText={
							errorInput === "barcodeInput"
								? t("common:validation.noBarcodeFound")
								: ""
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 6, md: 4 }} >
					<CatalogItemPickerProvider depotIds={[activeTask?.source]}>
					<Autocomplete
						id="itemInput"
						size="small"
						options={items}
						ListboxComponent={VirtualizedListbox}
						filterOptions={createFilterOptions((opt) => opt.ItemName || "")}
						{...catalogItemAutocompleteInteractionProps}
						getOptionLabel={(item) => item.ItemName || ""}
						isOptionEqualToValue={isCatalogItemOptionEqualToValue}
						renderOption={(props, option) => renderCatalogItemOption(props, option, "selling")}
						value={items.find((i) => i.id === selectedItem?.id) || null}
						onChange={(_, newValue, reason) => {
							handleCatalogItemAutocompleteChange(reason, newValue, {
								onSelect: (id) => onItemChanged(id),
								onClear: () => setSelectedItem(null),
							});
						}}
						loading={isItemLoading}
						renderInput={(params) => (
							<TextField
								{...params}
								label={
									<span>
											{t("common:ui.labels.item")}&nbsp;&nbsp;
										<RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
									</span>
								}
								placeholder={isItemLoading ? t("common:ui.placeholders.loadingItems") : t("common:ui.placeholders.selectItem")}
								required
								onFocus={(e) => e.target.select()}
								inputRef={itemRef}
								variant="filled"
								fullWidth
									helperText={selectedItem && isMetered ? t("common:ui.labels.stock", { value: Util.formatNumber(activeStock) }) : '-'}
							/>
						)}
						sx={{
							'& .MuiAutocomplete-listbox': {
								padding: 0,             // remove extra padding around list
							},
							'& .MuiAutocomplete-option': {
								padding: '1px 1px',     // make each option compact
								fontSize: '5rem',   // smaller font
								minHeight: 'auto',      // remove default minHeight
							},
							'& .MuiAutocomplete-popupIndicator': {
								padding: '4px',         // smaller icon padding
							},
						}}
						ListboxProps={{
							style: {
								maxHeight: 400,
							},
						}}
					/>
					</CatalogItemPickerProvider>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 2 }} >
					<QuantityPackHelper
						id="quantityInput"
						size="small"
						label={t("common:ui.labels.quantity")}
						fullWidth
						required
						disabled={!selectedItem}
						value={selectedItem?.quantity ?? 1}
						min={qtyMin(!!selectedItem?.isFractional)}
						max={quantityMax}
						fractional={selectedItem?.isFractional}
						inputRef={quantityRef}
						onKeyDown={onQuantityKeyDown}
						onChange={(newValue) => {
							if (validateQty(newValue)) {
								setSelectedItem((prev: Item | null) => {
									if (!prev) return null;
									return new Item({ ...prev, quantity: newValue });
								});
							}
						}}
						error={errorInput === "quantityInput"}
						helperText={errorInput === "quantityInput" ? errorMessageRef.current : undefined}
					/>
				</Grid>
				<Grid size={{ xs: 6, sm: 6, md: 2 }} >
					<TextField
						id="amountInput"
						size="small"
						label={t("common:ui.labels.amount")}
						variant="filled"
						fullWidth
						type="number"
						value={selectedItem?.price ?? 0}
						required
						inputRef={amountRef}
						onFocus={(e) => e.target.select()}
						disabled={!canEditSalePrice(access, lineAction)}
						inputProps={{
							min: canSellBelowMinPrice(access, lineAction)
								? 0
								: catalogMinSellPrice(selectedCatalogItem, selectedItem),
							max: MONEY_LIMITS.max,
							step: 0.01,
						}}
						onChange={(e) => {
							const parsed = parseMoneyInput(e.target.value);
							if (parsed === null) return;
							const newValue = roundMoney(parsed);
							if (validateAmt(newValue)) {
								setSelectedItem((prev: Item | null) => {
									if (!prev) return null;
									return new Item({ ...prev, price: newValue });
								});
							}
						}}
						error={errorInput === "amountInput"}
						helperText={
							errorInput === "amountInput"
								? errorMessageRef.current
								: selectedItem
									&& Number.isFinite(selectedItem.quantity)
									&& Number.isFinite(selectedItem.price)
									? t("common:ui.labels.totalWithValue", {
										value: Util.formatNumber(selectedItem.total),
									})
									: undefined
						}
					/>
				</Grid>
				<Grid size={{ xs: 12, sm: 12, md: 1 }} display="flex" justifyContent="center" alignItems="start">
					<Button fullWidth size="large" type="submit" color="success" startIcon={<Add />} sx={{ fontWeight: 'bolder' }} ref={addBtnRef}>
						{t("common:ui.buttons.add")}
					</Button>
				</Grid>
			</Grid>
		</form>
	);
};

export default SaleItemDetail;
